'use client';

import { shuffle } from 'lodash';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { trackEvent } from '@/analytics';
import { MenuButtonContent } from '@/components/SiteHeader/MenuButtonContent';
import { SiteHeaderDropdown } from '@/components/SiteHeader/SiteHeaderDropdown';
import { Wrapper } from '@/components/Wrapper';
import { topicPath } from '@/helpers/linkHelpers';
import Shuffle from '@/icons/react/Shuffle';
import type {
  SiteNavMenuItemType,
  SiteNavMenuType,
} from '@/lib/firestore/dataFetchers/getSiteNavMenu';
import { useSiteHeader } from '@/providers';

interface VerticalsMenuProps {
  headerData?: SiteNavMenuType;
}
export function VerticalsMenu({ headerData }: VerticalsMenuProps) {
  const pathname = usePathname();
  const { siteHeaderMenuRef } = useSiteHeader();
  const [randomTopic, setRandomTopic] = useState<SiteNavMenuItemType | null>(
    null,
  );

  useEffect(() => {
    const topicsToRandomize = headerData?.flatMap((item) => item.childTopics);
    const currentTopicPath = pathname?.split('/topics/')?.[1];
    const filteredTopics = topicsToRandomize?.filter(
      (topic) => !currentTopicPath?.includes(topic.slug || ''),
    );
    const randomTopic = shuffle(filteredTopics)[0];

    setRandomTopic(randomTopic);
  }, [headerData, pathname]);

  function handleRandomClick() {
    trackEvent('random_topic_button_click');
  }

  return (
    <div
      className="hidden md:block border-t border-primary"
      ref={siteHeaderMenuRef}
    >
      <Wrapper className="">
        <nav className="-mx-3">
          <ul className="flex items-center justify-between flex-wrap gap-x-0 lg:gap-x-3 xl:gap-x-6 gap-y-0">
            {headerData?.map(
              (group, index) =>
                group?.category?.id &&
                !!group?.childTopics?.length && (
                  <li key={group?.category?.id}>
                    <SiteHeaderDropdown
                      id={group.category.id}
                      category={group.category}
                      childrenTopics={group.childTopics}
                      comingSoonTopics={group.comingSoonTopics}
                      menuIndex={index}
                    />
                  </li>
                ),
            )}
            {randomTopic && (
              <li className="hidden xl:block">
                <Link
                  href={topicPath(randomTopic?.slug)}
                  className="block"
                  onClick={handleRandomClick}
                >
                  <MenuButtonContent
                    backgroundColor="bg-cyan-700"
                    textColor="text-cyan-600"
                  >
                    Random Topic <Shuffle className="translate-y-half" />
                  </MenuButtonContent>
                </Link>
              </li>
            )}
          </ul>
        </nav>
      </Wrapper>
    </div>
  );
}
